import { create } from 'zustand';
import { readStored, removeStored, writeStored } from '@/components/desktop/types';
import { onWindowMessage, tellOtherWindows } from './channel';

const STORAGE_KEY = 'poultry.selectedFarms';
const OLD_STORAGE_KEY = 'poultry.selectedFarm';

interface FarmScopeState {
  /** Empty means every farm the user is permitted to see. */
  selectedFarmIds: string[];
  select: (farmIds: string[]) => void;
  /** Reads the remembered farms again — after a different person signs in. */
  reload: () => void;
}

/**
 * The farms the user is currently looking at.
 *
 * This is a view filter, not a permission: it can only narrow what the server
 * already allows. Choosing farms never widens access, so it is safe to keep
 * in the browser.
 *
 * Remembered per person on this computer (two people sharing the office PC
 * each keep their own), and the same in every open window of the app: a
 * window showing one farm beside another showing all farms is how numbers get
 * read against the wrong place.
 */
export const useFarmScope = create<FarmScopeState>((set) => ({
  selectedFarmIds: read(),

  select: (farmIds) => {
    remember(farmIds);
    set({ selectedFarmIds: farmIds });
    tellOtherWindows({ type: 'farm', farmIds });
  },

  reload: () => set({ selectedFarmIds: read() }),
}));

onWindowMessage((m) => {
  if (m.type === 'farm') useFarmScope.setState({ selectedFarmIds: m.farmIds });
});

function remember(farmIds: string[]): void {
  if (farmIds.length === 0) removeStored(STORAGE_KEY);
  else writeStored(STORAGE_KEY, farmIds);
}

function read(): string[] {
  const value = readStored<unknown>(STORAGE_KEY, null);
  if (Array.isArray(value)) return value.map(String);

  // One-time migration from the single-farm key this replaced, so nobody's
  // choice is lost on the day this ships.
  const old = readStored<unknown>(OLD_STORAGE_KEY, null);
  if (typeof old === 'string' || typeof old === 'number') {
    removeStored(OLD_STORAGE_KEY);
    writeStored(STORAGE_KEY, [String(old)]);
    return [String(old)];
  }

  return [];
}

/** The query params for a request narrowed to these farms, or `{}` for all. */
export function farmScopeQuery(farmIds: readonly string[]): { farmIds?: string } {
  return farmIds.length ? { farmIds: farmIds.join(',') } : {};
}

export interface FarmOption {
  id: string;
  nameEn: string;
}

/**
 * What the top bar and a screen's "viewing" line call the current selection:
 * names up to two, then a count — "Sahiwal", "Sahiwal, Okara", "3 farms". An
 * empty selection, or one that happens to cover every farm there is, reads as
 * "All farms (12)" rather than spelling out every name.
 */
export function farmScopeLabel(farms: readonly FarmOption[], farmIds: readonly string[]): string {
  if (farmIds.length === 0 || farmIds.length >= farms.length) {
    return `All farms (${farms.length})`;
  }
  const names = farms.filter((f) => farmIds.includes(f.id)).map((f) => f.nameEn);
  if (names.length <= 2) return names.join(', ');
  return `${names.length} farms`;
}
